/**
 * Tiny shared building blocks for help articles. Importing the design tokens
 * direct from @realtyflows/ui in every article was noisy; this re-exports the
 * handful of styled wrappers we use repeatedly so articles stay prose-first.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { t, font } from '@realtyflows/ui';

export function H2({ children }) {
  return (
    <h2 style={{
      fontFamily: font.display, fontSize: 20, fontWeight: 700,
      color: t.textPrimary, margin: '28px 0 12px',
    }}>{children}</h2>
  );
}

export function P({ children }) {
  return <p style={{ margin: '0 0 14px', lineHeight: 1.7 }}>{children}</p>;
}

export function UL({ children }) {
  return <ul style={{ margin: '0 0 14px', paddingLeft: 22 }}>{children}</ul>;
}

export function OL({ children }) {
  return <ol style={{ margin: '0 0 14px', paddingLeft: 22 }}>{children}</ol>;
}

export function LI({ children }) {
  return <li style={{ marginBottom: 6 }}>{children}</li>;
}

export function Code({ children }) {
  return (
    <code style={{
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      background: t.surfaceWarm, color: t.textPrimary,
      padding: '2px 6px', borderRadius: 4, fontSize: 13,
    }}>{children}</code>
  );
}

export function Pre({ children }) {
  return (
    <pre style={{
      background: t.navy, color: '#E8EEFC',
      padding: '14px 18px', borderRadius: 8, overflowX: 'auto',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      fontSize: 13, lineHeight: 1.55, margin: '0 0 16px',
    }}><code>{children}</code></pre>
  );
}

export function Callout({ kind = 'info', children }) {
  const palette = {
    info:   { bg: t.skyLight,    border: t.sky,    icon: 'ⓘ' },
    tip:    { bg: t.goldPale,    border: t.gold,   icon: '✦' },
    warn:   { bg: t.coralLight,  border: t.coral,  icon: '!' },
  }[kind];
  return (
    <div style={{
      background: palette.bg,
      borderLeft: `3px solid ${palette.border}`,
      padding: '12px 16px', borderRadius: 6,
      margin: '0 0 16px',
      fontSize: 14, lineHeight: 1.6,
    }}>
      <span style={{ marginRight: 8, fontWeight: 700, color: palette.border }}>{palette.icon}</span>
      {children}
    </div>
  );
}

// In-app link to a feature route. Auth-gated routes will redirect to /login
// if the reader isn't signed in — that's fine, the login screen knows to
// bounce them back after.
export function FeatureLink({ to, children }) {
  return (
    <Link to={to} style={{ color: t.sky, textDecoration: 'none', fontWeight: 500 }}>
      {children}
    </Link>
  );
}
